import { Divider, Flex, Skeleton, Typography } from '@faceup/ui-base'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { HintIcon } from '../AdminLayout'

// Chrome does not make a scrollport keyboard-reachable unless it is focusable
type ScrollProps = { scroll: true; label: string } | { scroll?: false; label?: never }

const scrollClassName = 'not-print:lg:min-h-0 not-print:lg:grow not-print:lg:overflow-y-auto'

const scrollAttributes = (scroll: boolean | undefined, label: string | undefined) =>
  scroll && label ? { tabIndex: 0, role: 'region', 'aria-label': label } : {}

export type CardProps = {
  loading?: boolean
} & ScrollProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'title'>

const _Card = forwardRef<HTMLDivElement, CardProps>((props, ref) => {
  const { children, loading = false, className, scroll, label, ...otherProps } = props

  return (
    <div
      ref={ref}
      {...otherProps}
      {...scrollAttributes(scroll, label)}
      className={twMerge(
        'rounded-[20px] bg-white shadow-[0px_2px_20px_0px_rgba(22,25,84,0.1)]',
        scroll && scrollClassName,
        className
      )}
    >
      {loading ? <Skeleton className='p-6 pb-3' /> : children}
    </div>
  )
})

export type SectionProps = {
  children?: ReactNode
  loading?: boolean
}

export const Section = ({ children, loading = false }: SectionProps) => {
  return (
    <div className='group'>
      {loading ? <Skeleton className='p-6 pb-3' /> : children}
      <div className='mx-8 group-last-of-type:hidden'>
        <Divider />
      </div>
    </div>
  )
}

type HeaderProps = {
  children: ReactNode
  extra?: ReactNode
  hint?: ReactNode
}

const Header = ({ children, extra, hint }: HeaderProps) => (
  <div className='flex justify-between px-8 pt-8 pb-6'>
    <Typography.Title level={5} className='flex-auto'>
      <Flex align='center' gap='small'>
        {children}
        {hint && <HintIcon title={hint} />}
      </Flex>
    </Typography.Title>
    <Flex>{extra}</Flex>
  </div>
)

export type CardContentProps = ScrollProps & HTMLAttributes<HTMLDivElement>

const Content = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, scroll, label, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      {...scrollAttributes(scroll, label)}
      className={twMerge('relative flex flex-col px-8 pb-8', scroll && scrollClassName, className)}
    />
  )
)

type CardType = typeof _Card & {
  Section: typeof Section
  Header: typeof Header
  Content: typeof Content
}

_Card.displayName = 'Card'
Section.displayName = 'Card.Section'
Header.displayName = 'Card.Header'
Content.displayName = 'Card.Content'

export const Card: CardType = _Card as CardType

Card.Section = Section
Card.Header = Header
Card.Content = Content
